import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DocumentSignRecipientResponse } from '@/lib/api';
import { useRecipients, useSetRecipients } from '../hooks';
import { getRecipientColor } from './recipientColors';

interface DraftRecipient {
  /** Stable key for React; replaced with server-issued recipientId after save. */
  key: string;
  name: string;
  email: string;
  role: 'signer' | 'viewer' | 'approver';
}

interface RecipientsTableProps {
  envelopeId: string;
  /** When true, hide all editing affordances. Used once the envelope is sent. */
  readOnly?: boolean;
}

const ROLE_LABEL: Record<DraftRecipient['role'], string> = {
  signer: 'Signer',
  viewer: 'Viewer',
  approver: 'Approver',
};

let scratchKeyCounter = 0;
function nextScratchKey(): string {
  scratchKeyCounter += 1;
  return `new-${Date.now()}-${scratchKeyCounter}`;
}

function toDraft(r: DocumentSignRecipientResponse): DraftRecipient {
  return {
    key: r.recipientId,
    name: r.name ?? '',
    email: r.email ?? '',
    role: r.role,
  };
}

export function RecipientsTable({ envelopeId, readOnly = false }: RecipientsTableProps) {
  const { recipients, loading } = useRecipients(envelopeId);
  const setRecipients = useSetRecipients(envelopeId);

  const [drafts, setDrafts] = useState<DraftRecipient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Hydrate the form once the server list arrives. Subsequent updates from
  // the server replace the form unless the user has made local edits — that
  // way a refetch doesn't blow away in-progress changes.
  useEffect(() => {
    if (loading) return;
    if (!isDirty) {
      setDrafts(recipients.map(toDraft));
    }
  }, [recipients, loading, isDirty]);

  function update(index: number, patch: Partial<DraftRecipient>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
    setIsDirty(true);
  }

  function remove(index: number) {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  }

  function move(index: number, delta: -1 | 1) {
    setDrafts((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setIsDirty(true);
  }

  function add() {
    setDrafts((prev) => [...prev, { key: nextScratchKey(), name: '', email: '', role: 'signer' }]);
    setIsDirty(true);
  }

  async function save() {
    setError(null);

    const trimmed = drafts.map((d) => ({
      ...d,
      name: d.name.trim(),
      email: d.email.trim().toLowerCase(),
    }));

    for (const d of trimmed) {
      if (!d.name) {
        setError('Each recipient needs a name.');
        return;
      }
      if (!d.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) {
        setError(`Invalid email: ${d.email || '(empty)'}`);
        return;
      }
    }

    const seenEmails = new Set<string>();
    for (const d of trimmed) {
      if (seenEmails.has(d.email)) {
        setError(`Duplicate email: ${d.email}`);
        return;
      }
      seenEmails.add(d.email);
    }

    try {
      await setRecipients.mutateAsync(
        trimmed.map((d, i) => ({
          name: d.name,
          email: d.email,
          role: d.role,
          routingOrder: i,
        })),
      );
      setIsDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save recipients');
    }
  }

  if (loading && drafts.length === 0) {
    return <p className="text-sm text-muted-foreground">Loading recipients…</p>;
  }

  return (
    <div className="space-y-3">
      {drafts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No recipients yet.</p>
      ) : (
        <ul className="space-y-2">
          {drafts.map((draft, i) => {
            const color = getRecipientColor(i);
            return (
              <li
                key={draft.key}
                className="rounded-md border bg-card p-3 space-y-3 sm:space-y-0 sm:flex sm:items-end sm:gap-3"
              >
                <span
                  className="hidden sm:block h-9 w-2 rounded-sm"
                  aria-hidden="true"
                  style={{ background: color.swatch }}
                />
                <div className="flex-1 space-y-1">
                  <Label htmlFor={`recipient-name-${draft.key}`} className="text-xs">
                    Name
                  </Label>
                  <Input
                    id={`recipient-name-${draft.key}`}
                    value={draft.name}
                    maxLength={300}
                    onChange={(e) => update(i, { name: e.target.value })}
                    disabled={readOnly}
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor={`recipient-email-${draft.key}`} className="text-xs">
                    Email
                  </Label>
                  <Input
                    id={`recipient-email-${draft.key}`}
                    type="email"
                    value={draft.email}
                    maxLength={500}
                    onChange={(e) => update(i, { email: e.target.value })}
                    disabled={readOnly}
                  />
                </div>
                <div className="w-full sm:w-32 space-y-1">
                  <Label htmlFor={`recipient-role-${draft.key}`} className="text-xs">
                    Role
                  </Label>
                  <Select
                    value={draft.role}
                    onValueChange={(value) => update(i, { role: value as DraftRecipient['role'] })}
                    disabled={readOnly}
                  >
                    <SelectTrigger id={`recipient-role-${draft.key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(['signer', 'viewer', 'approver'] as const).map((role) => (
                        <SelectItem key={role} value={role}>
                          {ROLE_LABEL[role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {!readOnly && (
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                    >
                      <ChevronUp className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => move(i, 1)}
                      disabled={i === drafts.length - 1}
                      aria-label="Move down"
                    >
                      <ChevronDown className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(i)}
                      aria-label="Remove recipient"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!readOnly && (
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
            Add recipient
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={!isDirty || setRecipients.isPending}
          >
            Save recipients
          </Button>
        </div>
      )}
    </div>
  );
}
